import { expectAtIndex } from "@aprao/common/src/array/expectAtIndex.js";
import { trim } from "@aprao/common/src/array/trim.js";
import { assertUnreachable } from "@aprao/common/src/assertUnreachable.js";
import type { BigFraction } from "@aprao/common/src/math/BigFraction.js";
import { divide } from "@aprao/common/src/math/divide.js";
import {
  FRACTION_ONE,
  FRACTION_TWELVE,
  FRACTION_ZERO,
  fractionLte,
  fractionMax,
  fractionMin,
  fractionSum,
} from "@aprao/common/src/math/fraction.js";
import { multiply } from "@aprao/common/src/math/multiply.js";
import { opposite } from "@aprao/common/src/math/opposite.js";
import { sum } from "@aprao/common/src/math/sum.js";
import { combineNullables } from "@aprao/common/src/util/combineNullables.js";
import { times } from "@aprao/common/src/util/times.js";
import { makeAutoObservable } from "mobx";
import { mapOrForUndefinable } from "option-t/undefinable";
import { UnreachableCaseError } from "ts-essentials";

import type { CashflowPeriodGranularity } from "../shapes/ShapeAppraisal.js";
import type { AppraisalDocumentFacade } from "./AppraisalDocumentFacade.js";
import { computeAnnualIrr } from "./computeAnnualIrr.js";
import {
  balancesToCashflows,
  type CashflowFundSource,
  cashflowsToBalances,
  computeCashflow,
} from "./financeCashflowTools.js";
import type { FinanceFacade } from "./FinanceFacade.js";
import type { AnyLenderFacade } from "./lender/AnyLenderFacade.js";
import {
  computeInterestMonthlyRatio,
  type InterestLenderFacade,
} from "./lender/InterestLenderFacade.js";
import { interestLenderPredicate } from "./lender/interestLenderPredicate.js";
import type { ResidualFacade } from "./ResidualFacade.js";
import type { TaxFacade } from "./TaxFacade.js";

const tupleOfLenderWithInterestPredicate = <Rest extends Array<unknown>>(
  tuple: [AnyLenderFacade, ...Rest],
): tuple is [InterestLenderFacade, ...Rest] =>
  interestLenderPredicate(tuple[0]);

const interestCashflowsFromBalances = (
  lender: InterestLenderFacade,
  balances: ReadonlyArray<BigFraction>,
  granularity: CashflowPeriodGranularity,
) => {
  // Since the interest is expressed monthly, we need to adapt it to the cash flow
  // selected granularity.
  const granularityFactor =
    granularity === "monthly" ? FRACTION_ONE
    : granularity === "annual" ? FRACTION_TWELVE
    : assertUnreachable(granularity);

  const periodRatio = granularityFactor.mul(
    computeInterestMonthlyRatio(lender) ?? FRACTION_ZERO,
  );

  if (lender.interest.formulation === "drawnBalance") {
    return balances.map((balance) => balance.mul(periodRatio));
  } else {
    const availableBalance = fractionMin(FRACTION_ZERO, ...balances);

    return balances.map((balance) =>
      FRACTION_ZERO.equals(balance) ? FRACTION_ZERO : (
        availableBalance.mul(periodRatio)
      ),
    );
  }
};

export interface FlowScheduler {
  duration: number;
  getNetCashflowsAtIndex: (index: number) => {
    forwardFunded: BigFraction;
    debtFunded: BigFraction;
  };
}

export class FlowFacade {
  readonly $stores;

  constructor(stores: {
    documentFacade: AppraisalDocumentFacade;
    taxFacade: TaxFacade;
    residualFacade: ResidualFacade;
    financeFacade: FinanceFacade;
    flowScheduler: FlowScheduler;
  }) {
    makeAutoObservable(this);

    this.$stores = stores;
  }

  get projectCashflows() {
    return times(
      this.$stores.flowScheduler.duration,
      this.$stores.flowScheduler.getNetCashflowsAtIndex,
    );
  }

  /**
   * The calculation assumes that no debt is used for the project and is
   * therefore based on the net cash flow of each period.
   */
  get projectIrr() {
    return computeAnnualIrr(
      this.projectCashflows.map(({ debtFunded, forwardFunded }) =>
        debtFunded.add(forwardFunded),
      ),
      this.$stores.documentFacade.cashflowPeriodGranularity,
    );
  }

  /**
   * Finds the funding actors configuration that minimises uses of equity while
   * staying in the limits set by each actor's total facility.
   *
   * It runs a succession of cashflows until all the sources are reaching their
   * peak utilisation. It takes care of including the interest for the lenders
   * using a retained interest payout type. This is critical because the total
   * amount of interest has a direct influence on the available balance.
   */
  get financeCashflow() {
    const forwardProjectBalances = Object.freeze(
      cashflowsToBalances(
        this.projectCashflows.map(({ forwardFunded }) => forwardFunded),
      ),
    );

    // First run for the forward-funded part.
    const forwardCashflowRun = computeCashflow(forwardProjectBalances, [
      {
        key: "forwardFund" as const,
        availableBalance:
          this.$stores.documentFacade.getBuildPhaseCostTotalWithContingency(
            "forwardFunded",
          ) ?? FRACTION_ZERO,
      },
    ]);

    const [, forwardFundBalances] = expectAtIndex(
      forwardCashflowRun.lendersBalances,
      0,
    );

    // Then for the debt funded part reducing the balances with the profits from
    // the forward funded part.
    const debtProjectBalances = Object.freeze(
      cashflowsToBalances(
        this.projectCashflows.map(({ debtFunded }) => debtFunded),
      ),
    );

    const cashflowLenders = this.$stores.residualFacade.lenders.map(
      (lender): CashflowFundSource<AnyLenderFacade> => {
        const allowance =
          interestLenderPredicate(lender) ? lender.interestAllowance : null;

        return {
          key: lender,
          availableBalance: fractionMax(
            FRACTION_ZERO,
            sum([
              lender.facilitySize.relativesSet.getRelative("absolute"),
              opposite(allowance),
            ]) ?? FRACTION_ZERO,
          ),
        };
      },
    );

    const { equityBalances, lendersBalances } = computeCashflow(
      debtProjectBalances.map((balance, periodIndex) =>
        // Add the forward equity balance to the debt funded project cashflow since
        // the profits from the forward funded part can help to pay for the debt
        // funded part.
        balance.add(
          expectAtIndex(forwardCashflowRun.equityBalances, periodIndex),
        ),
      ),
      cashflowLenders,
      (
        this.$stores.documentFacade.cashflowOptimizationStrategy ===
          "minimizeFinance"
      ) ?
        (this.$stores.financeFacade.developerContribution ?? FRACTION_ZERO)
      : FRACTION_ZERO,
    );

    return {
      equityBalances,
      forwardFundBalances,
      lendersBalances,
    };
  }

  get positionsByPeriodIndex(): ReadonlyArray<{
    lenders: ReadonlyMap<AnyLenderFacade, BigFraction>;
    forwardFund: BigFraction;
  }> {
    const { lendersBalances, forwardFundBalances } = this.financeCashflow;

    return times(this.$stores.flowScheduler.duration, (periodIndex) => ({
      lenders: new Map(
        lendersBalances.map(([lender, balances]) => [
          lender,
          expectAtIndex(balances, periodIndex).neg(),
        ]),
      ),
      forwardFund: expectAtIndex(forwardFundBalances, periodIndex).neg(),
    }));
  }

  getTermInMonthsByLender = (lender: InterestLenderFacade) => {
    if (
      lender.interest.calculationType === "manual" ||
      lender.interest.calculationType === "auto"
    ) {
      return lender.definedTermInMonths;
    } else if (lender.interest.calculationType === "cashflow") {
      const granularity = this.$stores.documentFacade.cashflowPeriodGranularity;

      const granularityFactor =
        granularity === "monthly" ? 1
        : granularity === "annual" ? 12
        : assertUnreachable(granularity);

      const lenderHasPositionByPeriodIndex = this.positionsByPeriodIndex.map(
        ({ lenders }) =>
          mapOrForUndefinable(
            lenders.get(lender),
            // If the given lender is not the list of position, we just mark it
            // false, and it will be eventually trim in the next step below.
            false,
            (amount) => !FRACTION_ZERO.equals(amount),
          ),
      );

      return (
        // count the continuous section's length where the lender has a position
        trim(lenderHasPositionByPeriodIndex, false).length * granularityFactor
      );
    } else {
      return assertUnreachable(lender.interest.calculationType);
    }
  };

  /**
   * Gives the interests on a per-period basis based on the cashflow for each
   * lender.
   *
   * If the cashflow is not filled, then interest will show 0.
   */
  get interestByLenderByPeriodIndex(): ReadonlyArray<
    ReadonlyMap<InterestLenderFacade, BigFraction>
  > {
    const lenderActorCalculatorTuples = this.financeCashflow.lendersBalances
      .filter(tupleOfLenderWithInterestPredicate)
      .map(
        ([lender, balances]) =>
          [
            lender,
            interestCashflowsFromBalances(
              lender,
              balances,
              this.$stores.documentFacade.cashflowPeriodGranularity,
            ),
          ] as const,
      );

    return times(
      this.$stores.flowScheduler.duration,
      (periodIndex) =>
        new Map(
          lenderActorCalculatorTuples.map(([lender, interestsCashflows]) => [
            lender,
            expectAtIndex(interestsCashflows, periodIndex).neg(),
          ]),
        ),
    );
  }

  get interestByLender(): Map<InterestLenderFacade, BigFraction> {
    const totalByLender = new Map<InterestLenderFacade, BigFraction>();
    for (const interestByLender of this.interestByLenderByPeriodIndex) {
      for (const [lender, interest] of interestByLender.entries()) {
        totalByLender.set(
          lender,
          (totalByLender.get(lender) ?? FRACTION_ZERO).add(interest),
        );
      }
    }

    return totalByLender;
  }

  get interestPaymentByLenderByPeriodIndex(): ReadonlyArray<
    ReadonlyMap<InterestLenderFacade, BigFraction>
  > {
    return this.interestByLenderByPeriodIndex.map(
      (interestByLender, periodIndex, { length }) =>
        new Map(
          Array.from(interestByLender.entries(), ([lender, interest]) => {
            if (
              lender.interest.payoutType === "retained" ||
              lender.interest.payoutType === "rolledUp"
            ) {
              // last period we pay back the interest
              return [
                lender,
                periodIndex === length - 1 ?
                  (this.interestByLender.get(lender) ?? FRACTION_ZERO)
                : FRACTION_ZERO,
              ];
            } else if (lender.interest.payoutType === "serviced") {
              // every period we pay back the interest
              return [lender, interest];
            } else {
              return assertUnreachable(lender.interest.payoutType);
            }
          }),
        ),
    );
  }

  get lendersFeesTotalAssigned() {
    return fractionSum(
      times(
        this.$stores.flowScheduler.duration,
        this.$stores.financeFacade.getLendersFeesTotalAtPeriodIndex,
      ),
    );
  }

  get developerContributionByPeriodIndex() {
    return balancesToCashflows(this.financeCashflow.equityBalances).map(
      (cashflow, periodIndex, { length }) => {
        let contribution = cashflow.sub(
          this.$stores.financeFacade.getLendersFeesTotalAtPeriodIndex(
            periodIndex,
          ),
        );

        if (periodIndex === length - 1) {
          // We aggregate all interest into the final period to capture its impact
          // on profit. This makes sense for "rolled-up" and "retained" payouts,
          // but it's counterintuitive for "serviced" payouts, which normally
          // distribute interest each period. We do this so interest still affects
          // profit (assuming profit is realized in the last period) while
          // preventing developer contributions from covering interest every
          // period. That gives us a fixed equity contribution cap for the financial
          // optimization algorithm.
          // For clarity, we then add a separate row below developer contributions that
          // shows the total interest for all lenders using serviced payouts.
          contribution = contribution.sub(
            sum(Array.from(this.interestByLender.values())) ?? FRACTION_ZERO,
          );
        }

        return contribution;
      },
    );
  }

  /**
   * The calculation assumes that debt is used for the project and is therefore
   * based on the developer cash flow of each period (what the developers get
   * out of/into their pocket). Developer IRR (generally called Equity IRR) is
   * the "leveraged" version of project IRR.
   */
  get developerContributionIrr() {
    return computeAnnualIrr(
      this.developerContributionByPeriodIndex,
      this.$stores.documentFacade.cashflowPeriodGranularity,
    );
  }

  get cumulativeDeveloperContributionByPeriodIndex() {
    let cumulative = FRACTION_ZERO;
    return this.developerContributionByPeriodIndex.map(
      (cashflow) => (cumulative = cumulative.add(cashflow)),
    );
  }

  get developerPeakContribution() {
    return fractionMin(
      FRACTION_ZERO,
      ...this.cumulativeDeveloperContributionByPeriodIndex,
    ).neg();
  }

  getInterestAmountByLender = (lender: InterestLenderFacade) => {
    const interest = lender.interest;

    let interestAmount: BigFraction | null;

    if (interest.calculationType === "manual") {
      // an escape hatch when the user needs to override stamp-duty calculations
      interestAmount = interest.definedAmount;
    } else if (interest.calculationType === "auto") {
      // estimate interest amount
      interestAmount = lender.estimatedInterest;
    } else if (interest.calculationType === "cashflow") {
      interestAmount = this.interestByLender.get(lender) ?? null;
    } else {
      interestAmount = assertUnreachable(interest.calculationType);
    }

    return interestAmount;
  };

  getCostTotalByLender = (lender: AnyLenderFacade) =>
    sum([
      this.$stores.financeFacade.getLenderFeesTotalByLender(lender),
      interestLenderPredicate(lender) ?
        this.getInterestAmountByLender(lender)
      : null,
    ]);

  get financeCostsTotal() {
    return sum(
      this.$stores.residualFacade.lenders.map((lender) =>
        this.getCostTotalByLender(lender),
      ),
    );
  }

  getDevelopmentAndFinanceCostsTotal = (
    filterFundingType: "debtFunded" | "any",
  ) =>
    sum([
      this.$stores.residualFacade.getDevelopmentCostsTotal(filterFundingType),
      this.financeCostsTotal,
    ]);

  get developmentProfit() {
    return sum([
      this.$stores.taxFacade.netDevelopmentValue,
      this.$stores.documentFacade.getAdditionalIncomeTotal("any"),
      opposite(this.getDevelopmentAndFinanceCostsTotal("any")),
    ]);
  }

  get returnOnCost() {
    return divide(
      this.developmentProfit,
      this.getDevelopmentAndFinanceCostsTotal("any"),
    );
  }

  get returnOnGdv() {
    return divide(
      this.developmentProfit,
      this.$stores.documentFacade.stages.revenue.getGrossDevelopmentValue(
        "any",
      ),
    );
  }

  get returnOnDeveloperContribution() {
    return divide(
      this.developmentProfit,
      this.$stores.financeFacade.developerContribution,
    );
  }

  get targetProfit() {
    const { documentFacade } = this.$stores;

    if (documentFacade.returnTargetDenominator === "costs") {
      return multiply([
        documentFacade.returnTarget,
        this.getDevelopmentAndFinanceCostsTotal("any"),
      ]);
    } else if (documentFacade.returnTargetDenominator === "gdv") {
      return multiply([
        documentFacade.returnTarget,
        documentFacade.stages.revenue.getGrossDevelopmentValue("any"),
      ]);
    } else {
      throw new UnreachableCaseError(documentFacade.returnTargetDenominator);
    }
  }

  get targetProfitReached() {
    return (
      combineNullables(
        (target, actual) => fractionLte(target.round(2), actual.round(2)),
        this.targetProfit,
        this.developmentProfit,
      ) ?? true
    );
  }

  get statusCalculationResidualValue() {
    if (!this.targetProfitReached) {
      return "profitTooLow";
    }

    return "calculable";
  }

  getProfitShareAmountByLender = (
    lender: AnyLenderFacade,
  ): BigFraction | null =>
    lender.loanType === "profitShare" ?
      multiply([lender.profitShare, this.developmentProfit])
    : null;
}
